import { useSyncExternalStore } from 'react';

/**
 * **Is this tab running the dashboard the server now serves?** (2026-10-14)
 *
 * An open tab keeps the code it loaded until somebody reloads it, and the office
 * leaves tabs open for days: Lew answered a held list on 2026-10-13 from a tab
 * loaded the afternoon before, which predated the list's same-person button, so
 * the button was never on his screen. The server stamps every answer with the
 * entry bundle it serves (`X-Dashboard-Build`, `server/src/common/spa.ts`); this
 * compares it with the bundle this tab was booted from, and once they differ the
 * tab is stale for good — the shell reloads at the next navigation and shows a
 * notice until then (`components/stale-build.tsx`).
 *
 * Off the deploy path there is nothing to compare: the dev server boots from
 * `/src/main.tsx` and the API sends no stamp, so neither side has a build name.
 */

export const DASHBOARD_BUILD_HEADER = 'x-dashboard-build';

const ENTRY = /\/assets\/(index-[\w-]+\.js)$/;

/** The entry bundle among a page's module script addresses, or `null`. */
export function buildOf(scriptSources: readonly string[]): string | null {
  for (const source of scriptSources) {
    const name = ENTRY.exec(new URL(source, 'http://page.invalid').pathname)?.[1];
    if (name) return name;
  }
  return null;
}

/** The entry bundle this tab was booted from, off its own module script. */
function ownBuild(): string | null {
  return buildOf(
    Array.from(document.querySelectorAll<HTMLScriptElement>('script[type="module"][src]')).map(
      (script) => script.src,
    ),
  );
}

let stale = false;
let booted: string | null | undefined;
const listeners = new Set<() => void>();

/** Read off every API answer. A difference marks the tab stale; nothing unmarks it. */
export function noteServerBuild(served: string | null, own: () => string | null = ownBuild): void {
  if (stale || !served) return;
  if (booted === undefined) booted = own();
  if (!booted || served === booted) return;
  stale = true;
  for (const listener of listeners) listener();
}

export function isStaleBuild(): boolean {
  return stale;
}

export function useStaleBuild(): boolean {
  return useSyncExternalStore(
    (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    isStaleBuild,
    isStaleBuild,
  );
}

/** Tests only: forget what this module has seen. */
export function resetBuildForTests(): void {
  stale = false;
  booted = undefined;
}
